import type { Status } from '../../../games/spec/interpreter.ts';
import type { RulesSpec } from '../../../games/spec/schema.ts';
import { checkSnapshot, decodeStatus, isDeclaredSeat } from '../../../shared/protocol.ts';
import type { Seat } from '../../../shared/domain.ts';
import type { PlayerPublic, Seats, Snapshot, WireStatus } from '../../../shared/protocol.ts';

/**
 * The client's decode boundary: wire values in, engine values out.
 *
 * Every function takes the rules as its first argument rather than reading a
 * module-level constant. That is the whole point of this file now: the client
 * used to hold `ACTIVE_RULES = CONNECT4_SPEC.rules`, which quietly made one
 * game the only renderable game, on a platform whose premise is that games are
 * data. The rules a frame is checked against are the rules of the match being
 * played, fetched at runtime.
 *
 * Every one of them returns `null` rather than throwing. A frame that does not
 * match the active spec is not a crash; it means the client and server disagree
 * about the game, which the reducer already handles by resyncing.
 */

/** A seat this game declares, or null. */
export function decodeSeat(rules: RulesSpec, seat: number): Seat | null {
  return isDeclaredSeat(rules, seat) ? seat : null;
}

/**
 * The seat roster, rejected unless it covers exactly this game's seats.
 *
 * Keyed by seat number, so a three-seat spec produces three entries without
 * anything here counting to two.
 */
export function decodeSeats(rules: RulesSpec, seats: Seats): Record<Seat, PlayerPublic> | null {
  const out: Record<Seat, PlayerPublic> = {};
  for (const seat of rules.seats) {
    const player = seats[String(seat)];
    if (player === undefined) return null;
    out[seat] = player;
  }
  return out;
}

/** A status whose winner, line length and cell indices all fit this spec. */
export function decodeGameStatus(rules: RulesSpec, status: WireStatus): Status<RulesSpec> | null {
  const decoded = decodeStatus(rules, status);
  return decoded.ok ? decoded.status : null;
}

/** A snapshot whose board length, digits and status all fit this spec. */
export function isValidSnapshot(rules: RulesSpec, snapshot: Snapshot): boolean {
  return checkSnapshot(rules, snapshot).ok;
}
